import React, { useState, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { useWheelNavigation } from '../hooks/useWheelNavigation';
import { useSystemDate } from '../hooks/useSystemDate';

export const AttendanceManagementView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore((state) => state.activeCompany);
  const systemDate = useSystemDate();
  
  const employees = useLiveQuery(() => 
    activeCompany ? db.employees.where('companyId').equals(activeCompany.id!).and(e => e.status === 'Active').toArray() : []
  , [activeCompany]);

  const [selectedDate, setSelectedDate] = useState(systemDate);

  const attendances = useLiveQuery(() => 
    activeCompany ? db.employeeAttendance.where({ companyId: activeCompany.id!, date: selectedDate }).toArray() : []
  , [activeCompany, selectedDate]);

  const handleMarkAttendance = async (employeeId: number, status: 'Present' | 'Absent' | 'Half Day' | 'On Leave') => {
    if (!activeCompany) return;
    
    const existing = attendances?.find(a => a.employeeId === employeeId);
    
    if (existing) {
      await db.employeeAttendance.update(existing.id!, { status });
    } else {
      await db.employeeAttendance.add({
        companyId: activeCompany.id!,
        employeeId,
        date: selectedDate,
        status
      });
    }
  };

  return (
    <div className="flex flex-col w-full h-full bg-[#f8faf7] z-20 absolute inset-0 font-sans text-[13px] text-gray-800">
      <div className="bg-[#1e8585] text-white flex justify-between px-3 py-1 font-bold shadow-sm z-10 text-[14px]">
        <span className="w-1/3 text-left">Attendance Management</span>
        <span className="w-1/3 text-center uppercase">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      <div className="flex-1 p-4 overflow-hidden flex flex-col">
          <div className="mb-4 flex items-center gap-3">
             <label className="font-bold text-[#1e8585]">Select Date:</label>
             <input 
               type="date" 
               value={selectedDate} 
               onChange={e => setSelectedDate(e.target.value)}
               className="border border-[#1e8585] px-2 py-1 outline-none text-[13px]"
             />
          </div>

          <div className="bg-white border border-[#1e8585] flex-1 flex flex-col shadow-sm max-w-4xl">
             <div className="flex bg-[#d9e4d5] px-2 py-2 font-bold border-b border-gray-400 text-black">
                <div className="w-[200px]">Employee</div>
                <div className="w-[120px] text-center">Attendance</div>
                <div className="flex-1 pl-4">Actions (Mark)</div>
             </div>
             
             <div className="flex-1 overflow-y-auto">
               {(!employees || employees.length === 0) ? (
                 <div className="p-4 text-center text-gray-500 italic">No active employees found.</div>
               ) : (
                 employees.map((emp) => {
                   const att = attendances?.find(a => a.employeeId === emp.id);
                   const status = att?.status || 'Not Marked';
                   
                   return (
                     <div key={emp.id} className="flex px-2 py-2.5 border-b border-gray-200 items-center hover:bg-gray-50">
                        <div className="w-[200px] font-bold text-[#1e8585]">{emp.name}</div>
                        <div className="w-[120px] text-center font-bold">
                          {status === 'Present' && <span className="text-green-600">Present</span>}
                          {status === 'Absent' && <span className="text-red-600">Absent</span>}
                          {status === 'Half Day' && <span className="text-orange-500">Half Day</span>}
                          {status === 'On Leave' && <span className="text-blue-600">On Leave</span>}
                          {status === 'Not Marked' && <span className="text-gray-400">Not Marked</span>}
                        </div>
                        <div className="flex-1 flex gap-2 pl-4">
                           <button onClick={() => handleMarkAttendance(emp.id!, 'Present')} className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-800 border border-green-300 rounded text-[11px] font-bold">Present</button>
                           <button onClick={() => handleMarkAttendance(emp.id!, 'Absent')} className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-800 border border-red-300 rounded text-[11px] font-bold">Absent</button>
                           <button onClick={() => handleMarkAttendance(emp.id!, 'Half Day')} className="px-3 py-1 bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300 rounded text-[11px] font-bold">Half Day</button>
                           <button onClick={() => handleMarkAttendance(emp.id!, 'On Leave')} className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300 rounded text-[11px] font-bold">On Leave</button>
                        </div>
                     </div>
                   );
                 })
               )}
             </div>
          </div>
      </div>
    </div>
  );
};
